import { LogLevel } from '@optimizely/optimizely-sdk'
import { createInstance, enums } from '@optimizely/react-sdk'
import * as React from 'react'
import { useCookie } from 'react-use'
import { v4 as uuid } from 'uuid'
import { Configuration } from '@/scopes/common/domain/types/configuration'
import { isClient } from '@/utilities'
import { useAnalytics } from '../../analytics/hooks'

export const optimizelyClient = createInstance({
  sdkKey: process.env.NEXT_PUBLIC_OPTIMIZELY_SDK_KEY,
  logLevel: LogLevel.ERROR,
  odpOptions: {
    disabled: true,
  },
})

interface Notification {
  type: string
  userId: string
  decisionInfo: {
    flagKey: string
    enabled: boolean
    decisionEventDispatched: boolean
  }
}

export function useOptimizely(config: Configuration | null) {
  // NEXT_PUBLIC_ENABLE_OPTIMIZELY is used for testing purposes, to enable optimizely in test environments without the need of a config
  const isEnabled = config?.enableOptimizely || process.env.NEXT_PUBLIC_ENABLE_OPTIMIZELY === 'true'

  const [optimizelyUserId, setOptimizelyUserId] = useCookie('optimizelyEndUserId')

  const { sendEvent } = useAnalytics()

  // We have this code to write the cookie, because Optimize JS SDK decision, are triggering event network request outside the react lifecycle.
  // Thats why we can't put this code inside useEffect, this will provoke first events not being sent because the cookie is not set yet.
  // By setting the cookie here, we ensure that it's set before any decision is made
  if (isClient() && !optimizelyUserId && isEnabled) {
    // Optimizely cookie to expire in 90 days to keep user on the same A/B test variations for a longer period of time.
    setOptimizelyUserId(uuid(), { expires: 90, secure: true })
  }

  // Deduplicate CRO events by flagKey so only one fires per flag per page navigation.
  const sentFlagKeys = React.useRef(new Set<string>())

  const onDecision = React.useCallback(
    ({ type, decisionInfo }: Notification) => {
      // Guard: only send events if userId is available and not already sent for this flag
      if (type === 'flag' && optimizelyUserId && !sentFlagKeys.current.has(decisionInfo.flagKey) && decisionInfo.decisionEventDispatched) {
        sentFlagKeys.current.add(decisionInfo.flagKey)
        sendEvent({
          event: 'CRO',
          description: decisionInfo.flagKey,
          element: 'NL',
          type,
          result: decisionInfo.enabled ? 'Variant' : 'Default',
          interaction: false,
        })
      }
    },
    [sendEvent, optimizelyUserId],
  )

  React.useEffect(() => {
    const listenerId = optimizelyClient.notificationCenter.addNotificationListener<Notification>(
      enums.NOTIFICATION_TYPES.DECISION,
      onDecision,
    )

    return () => {
      optimizelyClient.notificationCenter.removeNotificationListener(listenerId)
    }
  }, [onDecision])

  return { userId: isEnabled ? optimizelyUserId : null }
}

export const OptimizelyFlagContext = React.createContext<Record<string, boolean>>({})

export const useOptimizelyFlagContext = () => React.use(OptimizelyFlagContext)
